/**
 * Billing Page Constants
 * Constants for billing and subscription management
 */
import { tr } from '../i18n/i18n';

/**
 * Tab options for the billing page
 */
export enum BILLING_TAB {
  PLANS = 'plans',
  BILLING = 'billing',
}

/**
 * Type for billing tab values
 */
export type BillingTabType = `${BILLING_TAB}`;

/**
 * Modal actions for student fees page
 * Used to queue modal openings after state updates
 */
export enum PAYMENT_MODAL_ACTION {
  COLLECT = 'collect',
  REVOKE = 'revoke',
  CANCEL = 'cancel',
  DELETE = 'delete',
  REFUND = 'refund',
}

/**
 * Invoice Status Enum
 * Matches backend INVOICE_STATUS enum values exactly
 */
export enum INVOICE_STATUS {
  PENDING = 'Pending',
  PAID = 'Paid',
  OVERDUE = 'Overdue',
  OVERDUE_PARTIAL = 'Overdue Partial',
  PARTIAL = 'Partial',
  CANCELLED = 'Cancelled',
}

/**
 * Raw invoice payment status values returned and accepted by the backend API.
 */
export enum INVOICE_STATUS_API {
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  OVERDUE_PARTIAL = 'OVERDUE_PARTIAL',
  PARTIAL = 'PARTIAL',
  CANCELLED = 'CANCELLED',
}

export const INVOICE_STATUS_LABELS: Record<INVOICE_STATUS_API, INVOICE_STATUS> = {
  [INVOICE_STATUS_API.PENDING]: INVOICE_STATUS.PENDING,
  [INVOICE_STATUS_API.PAID]: INVOICE_STATUS.PAID,
  [INVOICE_STATUS_API.OVERDUE]: INVOICE_STATUS.OVERDUE,
  [INVOICE_STATUS_API.OVERDUE_PARTIAL]: INVOICE_STATUS.OVERDUE_PARTIAL,
  [INVOICE_STATUS_API.PARTIAL]: INVOICE_STATUS.PARTIAL,
  [INVOICE_STATUS_API.CANCELLED]: INVOICE_STATUS.CANCELLED,
};

export const INVOICE_STATUS_COLORS: Record<INVOICE_STATUS_API, string> = {
  [INVOICE_STATUS_API.PENDING]: 'text-yellow-500',
  [INVOICE_STATUS_API.PAID]: 'text-green-600',
  [INVOICE_STATUS_API.OVERDUE]: 'text-red-600 font-bold',
  [INVOICE_STATUS_API.OVERDUE_PARTIAL]: 'text-red-600 font-bold',
  [INVOICE_STATUS_API.PARTIAL]: 'text-blue-600',
  [INVOICE_STATUS_API.CANCELLED]: 'text-red-600',
};

export const normalizeInvoiceStatus = (status?: string | null): INVOICE_STATUS_API | null => {
  if (!status) return null;

  const normalized = status.toUpperCase();
  if (normalized in INVOICE_STATUS_API) {
    return INVOICE_STATUS_API[normalized as keyof typeof INVOICE_STATUS_API];
  }

  const apiStatus = Object.entries(INVOICE_STATUS_LABELS).find(
    ([, label]) => label.toLowerCase() === status.toLowerCase(),
  )?.[0];

  return (apiStatus as INVOICE_STATUS_API | undefined) ?? null;
};

/**
 * Payment Status Enum
 * Calculated based on amount entered vs balance due
 * Used for dynamic UI rendering in payment modals
 */
export enum PAYMENT_STATUS {
  PARTIAL = 'partial',
  EXACT = 'exact',
  OVERPAYMENT = 'overpayment',
}

/**
 * Days of week options for Weekly frequency scheduling
 */
export const DAY_OF_WEEK_OPTIONS = [
  { label: tr('common.day_monday', 'Monday'), value: 0 },
  { label: tr('common.day_tuesday', 'Tuesday'), value: 1 },
  { label: tr('common.day_wednesday', 'Wednesday'), value: 2 },
  { label: tr('common.day_thursday', 'Thursday'), value: 3 },
  { label: tr('common.day_friday', 'Friday'), value: 4 },
  { label: tr('common.day_saturday', 'Saturday'), value: 5 },
  { label: tr('common.day_sunday', 'Sunday'), value: 6 },
];

/**
 * Month options for Yearly frequency scheduling
 */
export const MONTH_OPTIONS = [
  { label: tr('common.month_january', 'January'), value: 1 },
  { label: tr('common.month_february', 'February'), value: 2 },
  { label: tr('common.month_march', 'March'), value: 3 },
  { label: tr('common.month_april', 'April'), value: 4 },
  { label: tr('common.month_may', 'May'), value: 5 },
  { label: tr('common.month_june', 'June'), value: 6 },
  { label: tr('common.month_july', 'July'), value: 7 },
  { label: tr('common.month_august', 'August'), value: 8 },
  { label: tr('common.month_september', 'September'), value: 9 },
  { label: tr('common.month_october', 'October'), value: 10 },
  { label: tr('common.month_november', 'November'), value: 11 },
  { label: tr('common.month_december', 'December'), value: 12 },
];

/**
 * Tab options for invoice management page
 */
export enum INVOICE_MANAGEMENT_TAB {
  ASSIGN_INVOICES = 0,
  INVOICES = 1,
}

/**
 * Invoice Lifecycle Status Enum
 * Matches backend INVOICE_LIFECYCLE_STATUS Prisma enum
 */
export enum INVOICE_LIFECYCLE_STATUS {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Display labels for invoice lifecycle statuses
 */
export const INVOICE_LIFECYCLE_STATUS_LABELS: Record<INVOICE_LIFECYCLE_STATUS, string> = {
  [INVOICE_LIFECYCLE_STATUS.ACTIVE]: 'Active',
  [INVOICE_LIFECYCLE_STATUS.PAUSED]: 'Paused',
  [INVOICE_LIFECYCLE_STATUS.COMPLETED]: 'Completed',
  [INVOICE_LIFECYCLE_STATUS.CANCELLED]: 'Cancelled',
};

/**
 * Color classes for invoice lifecycle statuses
 */
export const INVOICE_LIFECYCLE_STATUS_COLORS: Record<INVOICE_LIFECYCLE_STATUS, string> = {
  [INVOICE_LIFECYCLE_STATUS.ACTIVE]: 'text-green-600',
  [INVOICE_LIFECYCLE_STATUS.PAUSED]: 'text-yellow-600',
  [INVOICE_LIFECYCLE_STATUS.COMPLETED]: 'text-blue-600',
  [INVOICE_LIFECYCLE_STATUS.CANCELLED]: 'text-red-600',
};

/**
 * UI Variant Enum
 * Used for styling invoice cards based on status
 */
export enum INVOICE_UI_VARIANT {
  UNPAID = 'unpaid',
  PAID = 'paid',
  PARTIAL = 'partial',
  OVERDUE = 'overdue',
  OVERDUE_PARTIAL = 'overdue_partial',
  CANCELLED = 'cancelled',
}
